import React, { useEffect, useState, useRef } from "react";
import JSON5 from "json5";
import DatePicker from "react-datepicker";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "react-toastify";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import onsiteproLogo from '../../assets/logo2 2.png';
import CheckTick from "../../assets/icons/Check-Tick.png";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import SearchableSiteSelect from "./SearchableSiteSelect";
import { ensureFormUuid, noopCreateSuccess } from "../../utils/formUuid";
import { buildSharePointFileName, toDdMmYyyy } from "../../utils/formPdfHelpers";
import "../forms/formTitle.css";
import "../forms/themeCheckbox.css";
import "../forms/themePlaceholder.css";
import "./horizonTable.css";

import { canEditDeleteRecords } from "../../utils/rolePermissions";
function parseSiteRegisterDate(value) {
  if (!value) return null;
  const s = String(value).trim();
  const slash = s.split("/");
  if (slash.length === 3) {
    const [dd, mm, yyyy] = slash;
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const dash = s.split("-");
  if (dash.length === 3) {
    if (dash[0].length === 4) {
      const d = new Date(Number(dash[0]), Number(dash[1]) - 1, Number(dash[2]));
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const [dd, mm, yyyy] = dash;
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function formatSiteRegisterDate(date) {
  if (!date) return "";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

const siteRegisterDatePickerProps = {
  dateFormat: "dd-MM-yyyy",
  className: "form-control",
  placeholderText: "dd-MM-yyyy",
  todayButton: "Today",
  calendarClassName: "site-register-theme-calendar",
  popperClassName: "site-register-datepicker-popper",
  popperPlacement: "bottom-start",
  popperProps: { strategy: "fixed" },
};

// Styled input for the "Filter by date" control so it matches the search box.
const DateFilterInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
  <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
    <i className="fa fa-calendar" style={{ position: "absolute", left: "12px", color: "#8b1634", fontSize: "14px", pointerEvents: "none" }} />
    <input
      ref={ref}
      value={value || ""}
      onClick={onClick}
      readOnly
      placeholder={placeholder}
      style={{ padding: "10px 12px 10px 34px", borderRadius: "12px", border: "1px solid #e2e8f0", fontSize: "14px", outline: "none", width: "190px", cursor: "pointer", background: "#fff" }}
    />
  </div>
));

const WAH_THEME = "#8b1634";
const wahEditLabelStyle = { fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6, display: "block" };
const wahEditInputStyle = {
  borderRadius: 8,
  border: "1px solid #e2e8f0",
  fontSize: 14,
  padding: "8px 12px",
};

function WorkAtHeightPage() {
  const canEditDelete = canEditDeleteRecords();
  const [records, setRecords] = useState([]);
  const [appUsers, setAppUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState(null);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [uploadingKey, setUploadingKey] = useState(null);

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [editRecord, setEditRecord] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  // SharePoint upload modal state
  const [showSharePointUploadModal, setShowSharePointUploadModal] = useState(false);
  const [sharePointUploadRecord, setSharePointUploadRecord] = useState(null);
  const [sharePointPhaseType, setSharePointPhaseType] = useState('phase');
  const [sharePointPhaseNumber, setSharePointPhaseNumber] = useState('15');
  const [sharePointNetwork, setSharePointNetwork] = useState('');
  const [pendingDirectUpload, setPendingDirectUpload] = useState(false);
  useEffect(() => {
    if (pendingDirectUpload && sharePointUploadRecord) {
      setPendingDirectUpload(false);
      // eslint-disable-next-line no-use-before-define
      handleSharePointUpload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDirectUpload, sharePointUploadRecord]);

  const [newRecord, setNewRecord] = useState({
    formUuid: "",
    userId: "",
    picosName: "",
    site: "",
    descriptionOfWah: "",
    dateIssued: "",
    startTime: "",
    finishTime: "",
    signature: "",
    checklistSections: []
  });
  const [editSignature, setEditSignature] = useState("");
  const sigCanvasRef = useRef(null);
  const editSigCanvasRef = useRef(null);

  // Helper function to convert date format from DD/MM/YYYY to YYYY-MM-DD for HTML date input
  const convertDateForInput = (dateString) => {
    if (!dateString) return '';
    // Check if it's already in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    // Convert from DD/MM/YYYY to YYYY-MM-DD
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      const [day, month, year] = dateString.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateString;
  };

  // Helper function to convert date format from YYYY-MM-DD to DD/MM/YYYY for saving
  const convertDateForSave = (dateString) => {
    if (!dateString) return '';
    // Check if it's already in DD/MM/YYYY format
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      return dateString;
    }
    // Convert from YYYY-MM-DD to DD/MM/YYYY
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    return dateString;
  };

  // Helper function to normalize malformed JSON strings - based on PPMReportPage approach
  const normalizeJsonString = (str) => {
    if (!str || typeof str !== 'string') return str;
    
    
    let s = str.trim();
    
    try {
      // Step 1: Fix boolean values first (before quoting)
      s = s.replace(/:\s*true(\s*[,}])/g, ':"true"$1');
      s = s.replace(/:\s*false(\s*[,}])/g, ':"false"$1');
      
      // Step 2: Quote unquoted keys (allowing / - _ ( ) in names)
      s = s.replace(/([,{]\s*)([A-Za-z0-9_\/\-\(\)]+)(\s*:)/g, '$1"$2"$3');
      
      // Step 3: Convert single-quoted strings to double-quoted safely
      s = s.replace(/'((?:\\'|[^'])*)'/g, (m, inner) => {
        const unescaped = inner.replace(/\\'/g, "'");
        const escapedDouble = unescaped.replace(/"/g, '\\"');
        return '"' + escapedDouble + '"';
      });
      
      // Step 4: Quote unquoted string values
      // Handle values that come after colons and are unquoted strings
      // Match: : value (where value is unquoted text until comma, closing brace, or bracket)
      s = s.replace(/:\s*([A-Za-z][^:,}\[\]]+?)(\s*[,}\]])/g, (match, value, ending) => {
        const trimmed = value.trim();
        // Don't quote if it's already a boolean, number, null, or starts with [ or {
        if (trimmed === 'true' || trimmed === 'false' || trimmed === 'null' || 
            /^\d+\.?\d*$/.test(trimmed) || trimmed.startsWith('[') || trimmed.startsWith('{')) {
          return match;
        }
        // Escape quotes in the value and add quotes
        const escaped = trimmed.replace(/"/g, '\\"');
        return `: "${escaped}"${ending}`;
      });
      
      // Step 5: Fix booleans back (unquote them)
      s = s.replace(/:\s*"true"(\s*[,}])/g, ': true$1');
      s = s.replace(/:\s*"false"(\s*[,}])/g, ': false$1');
      
      // Step 6: Remove trailing commas
      s = s.replace(/,\s*([}\]])/g, '$1');
      
      // Step 7: Fix the specific issue with "n/a" property
      s = s.replace(/"n\/a":/g, '"n/a":');
      
      return s;
      
    } catch (error) {
      return str;
    }
  };

  // Custom parser for malformed checklistSections - based on other forms
  const parseCustomChecklistFormat = (str) => {
    try {
      
      // Remove the outer brackets and split by sections
      const cleanStr = str.replace(/^\[|\]$/g, '');
      
      // Split by section boundaries (look for {header: pattern)
      const sections = [];
      let currentSection = '';
      let braceCount = 0;
      let inSection = false;
      
      for (let i = 0; i < cleanStr.length; i++) {
        const char = cleanStr[i];
        
        if (char === '{') {
          braceCount++;
          if (braceCount === 1) {
            inSection = true;
            currentSection = char;
          } else {
            currentSection += char;
          }
        } else if (char === '}') {
          braceCount--;
          currentSection += char;
          
          if (braceCount === 0 && inSection) {
            sections.push(currentSection);
            currentSection = '';
            inSection = false;
          }
        } else {
          if (inSection) {
            currentSection += char;
          }
        }
      }
      
      
      // Parse each section
      const parsedSections = sections.map(section => {
        try {
          
          // Extract header
          const headerMatch = section.match(/header:\s*([^,]+)/);
          const header = headerMatch ? headerMatch[1].trim().replace(/['"]/g, '') : '';
          
          // Extract items
          const itemsMatch = section.match(/items:\s*\[([^\]]*)\]/);
          const items = [];
          if (itemsMatch) {
            const itemsStr = itemsMatch[1];
            
            // Split items by looking for {title: pattern
            let itemStart = itemsStr.indexOf('{title:');
            while (itemStart !== -1) {
              let itemEnd = itemsStr.indexOf('}', itemStart);
              if (itemEnd === -1) break;
              
              const itemStr = itemsStr.substring(itemStart, itemEnd + 1);
              
              // Parse individual item
              // Fix: Capture full title including commas until we find ", value:" or "value:"
              const titleMatch = itemStr.match(/title:\s*(.+?)(?=\s*,\s*value:|\s*value:)/);
              const valueMatch = itemStr.match(/value:\s*([^,}]+)/);
              
              if (titleMatch && valueMatch) {
                const title = titleMatch[1].trim().replace(/['"]/g, '');
                const value = valueMatch[1].trim();
                
                // Convert value to boolean
                let boolValue = false;
                if (value === 'true' || value === true) {
                  boolValue = true;
                }
                
                items.push({
                  title: title,
                  value: boolValue
                });
                
              }
              
              itemStart = itemsStr.indexOf('{title:', itemEnd + 1);
            }
          }
          
          return {
            header: header,
            items: items,
            isOptional: false // Default value
          };
          
        } catch (sectionError) {
          return null;
        }
      }).filter(Boolean);
      
      return parsedSections;
      
    } catch (error) {
      return [];
    }
  };

  // Signature pad handlers for Add Modal
  const clearSignature = () => {
    if (sigCanvasRef.current) {
    sigCanvasRef.current.clear();
    }
    setNewRecord(prev => ({ ...prev, signature: "" }));
  };
  const saveSignature = () => {
    if (sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
      // Get the full canvas instead of trimmed to maintain proper size
      const canvas = sigCanvasRef.current.getCanvas();
      const sig = canvas.toDataURL("image/png");
      setNewRecord(prev => ({
        ...prev,
        signature: sig
      }));
    }
  };

  // Signature pad handlers for Edit Modal
  const clearEditSignature = () => {
    if (editSigCanvasRef.current) {
    editSigCanvasRef.current.clear();
    }
    setEditSignature("");
    setEditRecord(prev => ({ ...prev, signature: "" }));
  };
  const saveEditSignature = () => {
    if (editSigCanvasRef.current && !editSigCanvasRef.current.isEmpty()) {
      // Get the full canvas instead of trimmed to maintain proper size
      const canvas = editSigCanvasRef.current.getCanvas();
      const sig = canvas.toDataURL("image/png");
      setEditSignature(sig);
      setEditRecord(prev => ({ ...prev, signature: sig }));
    }
  };

  useEffect(() => {
    async function fetchRecords() {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const res = await fetch(baseUrl + "/api/work-at-height", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok && data.success && Array.isArray(data.data)) {
          setRecords(data.data);
        } else {
          setRecords([]);
        }
      } catch {
        setRecords([]);
      }
    }
    async function fetchAppUsers() {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const res = await fetch(baseUrl + "/api/app-users", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok && data.success && Array.isArray(data.data)) {
          setAppUsers(data.data);
        } else {
          setAppUsers([]);
        }
      } catch {
        setAppUsers([]);
      }
    }
    async function fetchSites() {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const res = await fetch(baseUrl + "/api/sites", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const data = await res.json();
        setSites(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
      } catch {
        setSites([]);
      }
    }
    fetchRecords();
    fetchAppUsers();
    fetchSites();
  }, []);

  // Search logic
  const filtered = records.filter(r => {
    // Date filter (compares against the record's Date Issued).
    if (dateFilter) {
      const filterYMD = `${dateFilter.getFullYear()}-${String(dateFilter.getMonth() + 1).padStart(2, '0')}-${String(dateFilter.getDate()).padStart(2, '0')}`;
      if (convertDateForInput(r.dateIssued) !== filterYMD) return false;
    }
    const fields = [r.picosName, r.site, r.descriptionOfWah, r.dateIssued].map(val => (val || '').toLowerCase());
    const searchWords = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (searchWords.length === 0) return true;
    return searchWords.every(word => fields.some(field => field.includes(word)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice().slice((page-1)*pageSize, page*pageSize);
  
  // Reset page to 1 if current page is beyond available pages
  if (filtered.length === 0 && page > 1) {
    setPage(1);
  }

  const formatDateForDisplay = (d) => {
    if (!d) return "—";
    const x = new Date(d);
    return isNaN(x.getTime()) ? d : x.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  // Add Record
  const handleAdd = async () => {
    if (!newRecord.picosName || !newRecord.descriptionOfWah || !newRecord.dateIssued || !newRecord.startTime || !newRecord.finishTime) {
      toast.error("All fields are required");
      return;
    }
    if (newRecord.finishTime <= newRecord.startTime) {
      toast.error("Finish Time must be after Start Time");
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      
      // Get user details for saving firstName and lastName
      const user = appUsers.find(u => u._id === newRecord.userId);
      const addedByFirstName = user ? user.firstName : '';
      const addedByLastName = user ? user.lastName : '';
      
      const payload = { 
        ...newRecord, 
        formUuid: ensureFormUuid(newRecord.formUuid),
        addedByFirstName,
        addedByLastName,
        checklistSections: JSON.stringify(newRecord.checklistSections) 
      };
      const res = await fetch(baseUrl + "/api/work-at-height", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        if (noopCreateSuccess(data)) {
          toast.warn(data.message || "This record already exists. No new record was added.");
          return;
        }
        setRecords([...records, data.data]);
        setShowAddModal(false);
        setNewRecord({
          formUuid: "",
          userId: "",
          picosName: "",
          site: "",
          descriptionOfWah: "",
          dateIssued: "",
          startTime: "",
          finishTime: "",
          signature: "",
          checklistSections: []
        });
        toast.success("Record added!");
      } else {
        toast.error(data.message || "Failed to add");
      }
    } catch {
      toast.error("Failed to add");
    }
  };

  // Edit Record
  const handleEdit = (rec) => {
    let checklistSections = [];
    try {
      let str = rec.checklistSections;
      if (typeof str === 'string') {
        // Try to parse as JSON first
        try {
          checklistSections = JSON.parse(str);
          if (!Array.isArray(checklistSections)) {
            checklistSections = [];
          }
        } catch (parseError) {
          // If JSON parsing fails, normalize the string and try again
          try {
            const normalizedStr = normalizeJsonString(str);
            checklistSections = JSON.parse(normalizedStr);
            if (!Array.isArray(checklistSections)) {
              checklistSections = [];
            }
          } catch (normalizedError) {
            // If normalized parsing fails, try JSON5 as fallback
          try {
            checklistSections = JSON5.parse(str);
            if (!Array.isArray(checklistSections)) {
              checklistSections = [];
            }
          } catch (json5Error) {
              // If all JSON parsing fails, try custom parser
              try {
                checklistSections = parseCustomChecklistFormat(str);
                if (!Array.isArray(checklistSections)) {
            checklistSections = [];
                }
              } catch (customError) {
                checklistSections = [];
              }
            }
          }
        }
      } else if (Array.isArray(str)) {
        checklistSections = str;
      }
    } catch (err) {
      checklistSections = [];
    }

    // Set editRecord with parsed checklistSections for dynamic rendering
    setEditRecord({
      ...rec,
      formUuid: ensureFormUuid(rec?.formUuid),
      checklistSections,
    });
    setShowEditModal(true);
    setTimeout(() => {
      if (editSigCanvasRef.current) {
        editSigCanvasRef.current.clear();
        if (rec.signature) {
        let sigSrc = rec.signature;
          if (!sigSrc.startsWith('data:image')) {
          sigSrc = 'data:image/png;base64,' + sigSrc;
        }
          const img = new window.Image();
          img.src = sigSrc;
          img.onload = () => {
            if (editSigCanvasRef.current) {
              const canvas = editSigCanvasRef.current.getCanvas();
              const ctx = canvas.getContext('2d');
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              // Draw the image with proper scaling to maintain aspect ratio
              const canvasAspect = canvas.width / canvas.height;
              const imgAspect = img.width / img.height;
              
              let drawWidth, drawHeight, drawX, drawY;
              
              if (imgAspect > canvasAspect) {
                // Image is wider than canvas
                drawWidth = canvas.width;
                drawHeight = canvas.width / imgAspect;
                drawX = 0;
                drawY = (canvas.height - drawHeight) / 2;
              } else {
                // Image is taller than canvas
                drawHeight = canvas.height;
                drawWidth = canvas.height * imgAspect;
                drawX = (canvas.width - drawWidth) / 2;
                drawY = 0;
              }
              
              ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
            }
          };
          img.onerror = () => {
            console.error('Failed to load signature image');
          };
        }
      }
    }, 300);
  }
  const handleEditSave = async () => {
    if (!editRecord.picosName || !editRecord.descriptionOfWah || !editRecord.dateIssued || !editRecord.startTime || !editRecord.finishTime) {
      toast.error("All fields are required");
      return;
    }
    if (editRecord.finishTime <= editRecord.startTime) {
      toast.error("Finish Time must be after Start Time");
      return;
    }
    
    // Use the checklistSections directly from editRecord (no mapping needed)
    const updatedChecklistSections = Array.isArray(editRecord.checklistSections) ? 
      JSON.parse(JSON.stringify(editRecord.checklistSections)) : [];
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      
      // Get user details for saving firstName and lastName
      const user = appUsers.find(u => u._id === editRecord.userId);
      const addedByFirstName = user ? user.firstName : '';
      const addedByLastName = user ? user.lastName : '';
      
      const payload = { 
        ...editRecord, 
        formUuid: ensureFormUuid(editRecord.formUuid),
        addedByFirstName,
        addedByLastName,
        checklistSections: JSON.stringify(updatedChecklistSections) 
      };
      const res = await fetch(baseUrl + `/api/work-at-height/${editRecord._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRecords(records.map(r => r._id === editRecord._id ? { ...editRecord, checklistSections: updatedChecklistSections } : r));
        setShowEditModal(false);
        toast.success("Record updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  // SharePoint Upload Handler
  const handleSharePointUpload = async () => {
    const locationForUpload = (sharePointUploadRecord?.picosName || '').trim();

    if (!locationForUpload) {
      toast.error('Picos Name is missing for this record.');
      return;
    }

    if (sharePointPhaseType === 'phase' && (!sharePointNetwork || sharePointNetwork.trim() === '')) {
      toast.error('Please select an area (EPN, LPN, or SPN)');
      return;
    }

    const payloadPhaseType = sharePointPhaseType === 'phase' ? 'phase' : 'non-phase';
    const payloadNetwork = sharePointPhaseType === 'phase' ? sharePointNetwork : '';
    const payloadPhaseNumber = sharePointPhaseType === 'phase' ? sharePointPhaseNumber : '';

    setShowSharePointUploadModal(false);

    await generatePDF(sharePointUploadRecord, {
      uploadOnly: true,
      phaseType: payloadPhaseType,
      phaseNumber: payloadPhaseNumber,
      network: payloadNetwork,
      siteName: locationForUpload,
      onSuccess: () => {
        setSharePointUploadRecord(null);
        setSharePointPhaseType('phase');
        setSharePointPhaseNumber('15');
        setSharePointNetwork('');
      },
      onError: () => {}
    });
  };

  // Delete Record
  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };
  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(baseUrl + `/api/work-at-height/${deleteId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRecords(records.filter(r => r._id !== deleteId));
        toast.success("Deleted successfully!");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  // Checklist item handler
  const handleChecklistChange = (sectionIdx, itemIdx, isEdit = false) => {
    if (isEdit) {
      const updatedSections = [...editRecord.checklistSections];
      updatedSections[sectionIdx].items[itemIdx].value = !updatedSections[sectionIdx].items[itemIdx].value;
      setEditRecord({ ...editRecord, checklistSections: updatedSections });
    } else {
      const updatedSections = [...newRecord.checklistSections];
      updatedSections[sectionIdx].items[itemIdx].value = !updatedSections[sectionIdx].items[itemIdx].value;
      setNewRecord({ ...newRecord, checklistSections: updatedSections });
    }
  };

  // Text handler
  const handleTextChange = (field, value, isEdit = false) => {
    if (isEdit) {
      setEditRecord({ ...editRecord, [field]: value });
    } else {
      setNewRecord({ ...newRecord, [field]: value });
    }
  };

  const generatePDF = async (record, options = {}) => {
    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint ? {
      phaseType: options.phaseType || 'non-phase',
      phaseNumber: options.phaseNumber || '',
      network: options.network || '',
      siteName: options.siteName || '',
      onSuccess: options.onSuccess,
      onError: options.onError
    } : null;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Modern color scheme
    const primaryColor = [139, 22, 52]; // #8b1634
    const secondaryColor = [233, 207, 214]; // #e9cfd6
    const headerColor = [139, 22, 52]; // Dark magenta
    
    // Helper function to draw modern rounded rectangle
    const drawRoundedRect = (x, y, width, height, radius, fillColor, strokeColor, hasShadow = false) => {
      if (hasShadow) {
        doc.setFillColor(0, 0, 0, 0.1);
        doc.roundedRect(x + 1, y + 1, width, height, radius, radius, 'F');
      }
      doc.setFillColor(...fillColor);
      doc.setDrawColor(...strokeColor);
      doc.setLineWidth(0.5);
      doc.roundedRect(x, y, width, height, radius, radius, 'FD');
    };
    
    // ---- HEADER SECTION ----
    const headerHeight = 30;
    drawRoundedRect(0, 0, pageWidth, headerHeight, 0, primaryColor, primaryColor);
    
    // ---- ADD HIGH-RES LOGO ----
    const addLogoToPDF = () => {
      return new Promise((resolve) => {
        const logoImg = new Image();
        logoImg.crossOrigin = 'anonymous';
        logoImg.onload = function () {
          try {
            const logoWidth = 70;
            const aspectRatio = logoImg.height / logoImg.width;
            const logoHeight = logoWidth * aspectRatio;
            const logoX = 10;
            const logoY = (headerHeight - logoHeight) / 2;
            
            const canvas = document.createElement('canvas');
            const scale = 2.5;
            canvas.width = logoImg.width * scale;
            canvas.height = logoImg.height * scale;
            const ctx = canvas.getContext('2d');
            ctx.scale(scale, scale);
            ctx.drawImage(logoImg, 0, 0);
            
            const highResDataUrl = canvas.toDataURL('image/png', 1.0);
            doc.addImage(highResDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight, '', 'NONE');
          } catch (error) {
            doc.setFillColor(255, 255, 255);
            doc.rect(10, 6, 60, 15, 'F');
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
            doc.text("TAYLOR TECHNOLOGY", 15, 12);
            doc.text("SYSTEMS LIMITED", 15, 17);
          }
          resolve();
        };
        logoImg.onerror = function () {
          doc.setFillColor(255, 255, 255);
          doc.rect(10, 6, 60, 15, 'F');
          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
          doc.setTextColor(0, 0, 0);
          doc.text("TAYLOR TECHNOLOGY", 15, 12);
          doc.text("SYSTEMS LIMITED", 15, 17);
          resolve();
        };
        logoImg.src = onsiteproLogo;
      });
    };
    
    await addLogoToPDF();
    
    // ---- HEADER TEXT ----
    const titleX = 90;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.text("Work At Height Assessment", titleX, 13);
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("TAYLOR TECHNOLOGY SYSTEMS LIMITED", titleX, 19);
    
    let yPosition = headerHeight + 10;
    
    // ---- Section 1: Basic Information ----
    doc.setFillColor(139, 22, 52);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    
    doc.rect(10, yPosition, pageWidth - 20, 8, "F");
    doc.text("1", 14, yPosition + 5.5);
    doc.text("Basic Information", 22, yPosition + 5.5);
    yPosition += 8;
    
    autoTable(doc, {
      startY: yPosition,
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 70 },
        2: { cellWidth: 30 },
        3: { cellWidth: 40 },
      },
      head: [
        [
          { content: "PICOS Name:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
          { content: record.picosName || "", styles: { halign: "center" } },
          { content: "Date Issued:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
          { content: record.dateIssued || "", styles: { halign: "center" } },
        ],
      ],
      body: [
        [
          { content: "Description:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
          { content: record.descriptionOfWah || "", styles: { halign: "center" } },
          { content: "Start Time:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
          { content: record.startTime || "", styles: { halign: "center" } },
        ],
        [
          { content: "Finish Time:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
          { content: record.finishTime || "", styles: { halign: "center" } },
          { content: "Site:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
          { content: record.site || "", styles: { halign: "center" } },
        ],
      ],
      styles: {
        font: "helvetica",
        fontSize: 9,
        cellPadding: 3,
        overflow: "linebreak",
        valign: "middle",
      },
      theme: "grid",
      tableWidth: pageWidth - 20,
      margin: { left: 10 },
      headStyles: {
        fillColor: [245, 245, 245],
        textColor: [0, 0, 0],
        lineWidth: 0.2,
      },
      bodyStyles: {
        lineWidth: 0.2,
        textColor: [0, 0, 0],
      },
    });
    
    yPosition = doc.lastAutoTable.finalY + 2;
    
    // ---- Section 2: Checklist Sections ----
    let checklistSections = [];
    try {
      let str = record.checklistSections;
      if (typeof str === 'string') {
        // Remove outer quotes if present (the string might be quoted JSON)
        str = str.trim();
        if (str.startsWith('"') && str.endsWith('"')) {
          str = str.slice(1, -1);
        }
        
        // Try to parse as JSON first
        try {
          checklistSections = JSON.parse(str);
          if (!Array.isArray(checklistSections)) {
            checklistSections = [];
          }
        } catch (parseError) {
          // If JSON parsing fails, normalize the string and try again
          try {
            const normalizedStr = normalizeJsonString(str);
            checklistSections = JSON.parse(normalizedStr);
            if (!Array.isArray(checklistSections)) {
              checklistSections = [];
            }
          } catch (normalizedError) {
            // If normalized parsing fails, try JSON5 as fallback
            try {
              checklistSections = JSON5.parse(str);
              if (!Array.isArray(checklistSections)) {
                checklistSections = [];
              }
            } catch (json5Error) {
              // If all JSON parsing fails, try custom parser
              try {
                checklistSections = parseCustomChecklistFormat(str);
                if (!Array.isArray(checklistSections)) {
                  checklistSections = [];
                }
              } catch (customError) {
                checklistSections = [];
              }
            }
          }
        }
      } else if (Array.isArray(str)) {
        checklistSections = str;
      }
    } catch (err) {
      checklistSections = [];
    }
    
    if (checklistSections && checklistSections.length > 0) {
      checklistSections.forEach((section, sectionIdx) => {
        // Check if we need a new page
        if (yPosition + 50 > pageHeight - 30) {
          doc.addPage();
          yPosition = 20;
        }
        
        // Section Header
        doc.setFillColor(139, 22, 52);
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.rect(10, yPosition, pageWidth - 20, 8, "F");
        doc.text(`${sectionIdx + 2}`, 14, yPosition + 5.5);
        doc.text(section.header || `Section ${sectionIdx + 1}`, 22, yPosition + 5.5);
        yPosition += 8;
        
        // Checklist Items
        if (section.items && Array.isArray(section.items) && section.items.length > 0) {
          const tableBody = section.items.map(item => [
            { content: item.title || '', styles: { halign: "left" } },
            { content: '', styles: { halign: "center" } } // Empty content, will be drawn as image
          ]);
          
          // Calculate column widths: 85% for title, 15% for checkbox
          const tableWidth = pageWidth - 20;
          const titleColWidth = tableWidth * 0.85;
          const checkboxColWidth = tableWidth * 0.15;
          
          autoTable(doc, {
            startY: yPosition,
            columnStyles: {
              0: { cellWidth: titleColWidth, overflow: 'linebreak', halign: 'left' },
              1: { cellWidth: checkboxColWidth, halign: 'center' },
            },
            head: [
              [
                { content: "Checklist Item", styles: { fillColor: [240, 240, 240], fontStyle: "bold" } },
                { content: "Completed", styles: { fillColor: [240, 240, 240], fontStyle: "bold", halign: "center" } },
              ],
            ],
            body: tableBody,
            didDrawCell: (data) => {
              if (data.section === 'body' && data.column.index === 1) {
                const item = section.items[data.row.index];
                if (item && (item.value === true || item.value === 'true' || item.value === 1)) {
                  const cell = data.cell;
                  const centerX = cell.x + cell.width / 2;
                  const centerY = cell.y + cell.height / 2;
                  doc.addImage(CheckTick, "PNG", centerX - 2, centerY - 2, 4, 4);
                }
              }
            },
            styles: {
              font: "helvetica",
              fontSize: 9,
              cellPadding: 4,
              halign: "left",
              valign: "middle",
              overflow: "linebreak",
            },
            theme: "grid",
            tableWidth: tableWidth,
            margin: { left: 10 },
            headStyles: {
              textColor: [0, 0, 0],
              fillColor: [245, 245, 245],
              lineWidth: 0.2,
            },
            bodyStyles: {
              lineWidth: 0.2,
            },
          });
          
          yPosition = doc.lastAutoTable.finalY + 2;
        }
      });
    }
    
   // ---- Section 6 : Signatories ----
doc.setFillColor(139, 22, 52); // Header background
doc.setTextColor(255, 255, 255);
doc.setFont("helvetica", "bold");
doc.rect(10, yPosition, pageWidth - 20, 10, "F");
doc.text("Section 6 : Signatories", 15, yPosition + 7);
yPosition += 18;

// ✅ Signature box (half width)
const boxWidth = (pageWidth - 20) / 2; // half width
const boxHeight = 50;
doc.setDrawColor(200, 200, 200);
doc.setFillColor(248, 248, 248);
doc.roundedRect(10, yPosition, boxWidth, boxHeight, 3, 3, "FD");

// Label
doc.setTextColor(139, 22, 52);
doc.setFontSize(12);
doc.text("Signature", 15, yPosition + 12);

// Signature image
if (record.signature) {
  const imgWidth = 35;  // controlled smaller width
  const imgHeight = 20;
  const imgX = 15;
  const imgY = yPosition + 15;
  doc.addImage(record.signature, "PNG", imgX, imgY, imgWidth, imgHeight);
}

yPosition += boxHeight + 10; // move down for next section
    
    // ---- Footer Section ----
    const footerY = pageHeight - 25;
    drawRoundedRect(0, footerY, pageWidth, 25, 0, [248, 249, 250], [200, 200, 200]);
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`,
      20,
      footerY + 8
    );
    doc.text("Page 1 of 1", pageWidth - 30, footerY + 15);
    
    // Build filename and either save or upload
    const safeName = String(record.picosName || 'WorkAtHeight').replace(/[\\/:"*?<>|]+/g, '-');
    const safeDate = String(record.dateIssued || 'Assessment').replace(/[\\/:"*?<>|]+/g, '-');

    let fileName;
    if (shouldUploadToSharePoint && uploadConfig) {
      fileName = buildSharePointFileName([
        record.picosName,
        record.site,
        toDdMmYyyy(record.dateIssued),
      ]);
    } else {
      fileName = `WAtH_${safeName}_${safeDate}.pdf`;
    }
    
    if (shouldUploadToSharePoint && uploadConfig) {
      try {
        const key = `${record._id || ''}|${record.picosName || ''}|${record.dateIssued || ''}`;
        setUploadingKey(key);
        const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
        const dataUri = doc.output('datauristring');
        
        // Prepare SharePoint upload parameters
        const uploadParams = {
          fileName,
          fileBase64: dataUri,
          phaseType: uploadConfig.phaseType,
          phaseNumber: uploadConfig.phaseNumber,
          network: uploadConfig.network,
          siteName: uploadConfig.siteName, uploadContext: "hseq-forms-tablets", uploadSubFolder: "Work At Height"
        };
        
        const res = await fetch(`${apiUrl}/api/sharepoint/upload`, {
          method: 'POST',
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
          body: JSON.stringify(uploadParams)
        });
        if (res.status !== 204 && !res.ok) {
          let msg = 'SharePoint upload failed';
          try {
            const out = await res.json();
            msg = out?.message || msg;
          } catch (_) {}
          throw new Error(msg);
        }
        toast.success("PDF uploaded to SharePoint successfully!");
        if (uploadConfig.onSuccess) {
          uploadConfig.onSuccess();
        }
      } catch (err) {
        toast.error(err?.message || "SharePoint upload failed");
        if (uploadConfig.onError) {
          uploadConfig.onError(err);
        }
      } finally {
        setUploadingKey(null);
      }
    } else {
      doc.save(fileName);
      toast.success("Professional Work At Height PDF generated successfully!");
    }
  };

  return (
    <>
      <div style={{
        background: '#ffffff',
        borderRadius: '20px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        padding: '24px',
        marginBottom: '24px'
      }}>
        {/* Header Section */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#1a202c',
            margin: 0
          }}>Work At Height</h2>
          
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                style={{
                  background: "#8b1634",
                  color: "#fff",
                  border: "none",
                  borderRadius: "10px",
                  padding: "10px 14px",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Import File
              </button>
            )}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <DatePicker
              selected={dateFilter}
              onChange={(d) => { setDateFilter(d); setPage(1); }}
              dateFormat="dd-MM-yyyy"
              placeholderText="Filter by date"
              customInput={<DateFilterInput placeholder="Filter by date" />}
              calendarClassName="site-register-theme-calendar"
              popperClassName="site-register-datepicker-popper"
              popperPlacement="bottom-start"
              todayButton="Today"
            />
            {dateFilter && (
              <button
                type="button"
                onClick={() => { setDateFilter(null); setPage(1); }}
                title="Clear date"
                style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', color: '#8b1634', cursor: 'pointer', fontSize: '16px', lineHeight: 1, padding: 0 }}
              >
                ×
              </button>
            )}
          </div>
          <div style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center'
          }}>
            <i className="fa fa-search" style={{
              position: 'absolute',
              left: '12px',
              color: '#8b1634',
              fontSize: '14px'
            }}></i>
            <input
              type="text"
              placeholder="Type to Search"
              value={search} 
              onChange={e=>{ setSearch(e.target.value); setPage(1); }}
              style={{
                padding: '10px 12px 10px 36px',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                fontSize: '14px',
                outline: 'none',
                width: '250px',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#8b1634'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>
          </div>
        </div>

        {/* Table Container */}
        <div style={{
          overflowX: 'auto',
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}>
          {/* Table Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1.5fr 1fr 0.8fr 0.8fr 1fr 1fr 1.2fr',
            background: '#f7fafc',
            padding: '12px 16px',
            borderBottom: '1px solid #e2e8f0',
            gap: '16px',
            alignItems: 'center'
          }}>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Picos Name</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Site</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Description</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Date Issued</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Start Time</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Finish Time</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Added By</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Updated Date</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Actions</div>
          </div>

          {/* Table Body */}
          <div>
            {paged.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#718096',
                fontSize: '16px'
              }}>No records found.</div>
            ) : paged.map((r, idx) => {
                // First check if firstName and lastName are saved in the record
                // If not, try to find user from appUsers array
                let userName = '-';
                if (r.addedByFirstName && r.addedByLastName) {
                  userName = `${r.addedByFirstName} ${r.addedByLastName}`;
                } else {
                  const user = appUsers.find(u => u._id === r.userId);
                  userName = user ? `${user.firstName} ${user.lastName}` : (r.userId || '-');
                }
                return (
                  <div 
                    key={r._id || idx}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1.5fr 1fr 0.8fr 0.8fr 1fr 1fr 1.2fr',
                      padding: '16px',
                      borderBottom: '1px solid #f1f5f9',
                      gap: '16px',
                      alignItems: 'center',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.picosName || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.site || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.descriptionOfWah || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.dateIssued || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.startTime || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.finishTime || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{userName || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{formatDateForDisplay(r.updatedAt || r.createdAt)}</div>
                    <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                  {canEditDelete && (
                    <button 
                      onClick={()=>handleEdit(r)}
                      title="Edit"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '6px',
                        borderRadius: '6px',
                        transition: 'background-color 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0fdf4'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <i className="fa fa-edit" style={{color:'#22c55e', fontSize: '16px'}}></i>
                    </button>
                  )}
                  <button 
                    onClick={()=>generatePDF(r)}
                    title="Generate PDF"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '6px',
                      borderRadius: '6px',
                      transition: 'background-color 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <i className="fa fa-file-pdf-o" style={{color:'#dc2626', fontSize: '16px'}}></i>
                  </button>
                  {(() => { 
                    const k = `${r._id || ''}|${r.picosName || ''}|${r.dateIssued || ''}`; 
                    const busy = uploadingKey === k; 
                    return (
                      <button 
                        onClick={()=>{
                          if (!busy) {
                            setSharePointUploadRecord(r);
                            setSharePointPhaseType('phase');
                            setSharePointPhaseNumber('15');
                            setSharePointNetwork('');
                            setSharePointPhaseType("non-phase"); setPendingDirectUpload(true);
                          }
                        }}
                        disabled={busy}
                        title={busy ? "Uploading..." : "Upload to SharePoint"}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: busy ? 'not-allowed' : 'pointer',
                          padding: '6px',
                          borderRadius: '6px',
                          transition: 'background-color 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: busy ? 0.6 : 1
                        }}
                        onMouseEnter={(e) => !busy && (e.currentTarget.style.backgroundColor = '#f0fdf4')}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{color:'#22c55e', fontSize: '16px'}}></i>
                      </button>
                    ); 
                  })()}
                  {canEditDelete && (
                    <button 
                      onClick={()=>handleDelete(r._id)}
                      title="Delete"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '6px',
                        borderRadius: '6px',
                        transition: 'background-color 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <i className="fa fa-trash" style={{color:'#e53e3e', fontSize: '16px'}}></i>
                    </button>
                  )}
                </div>
              </div>
                );
            })}
          </div>
        </div>

        {/* Pagination */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginTop: '18px',
          gap: '12px'
        }}>
          <button 
            className="horizon-table-btn" 
            disabled={page===1 || filtered.length === 0} 
            onClick={()=>setPage(page-1)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              background: (page===1 || filtered.length === 0) ? '#f7fafc' : '#ffffff',
              color: (page===1 || filtered.length === 0) ? '#a0aec0' : '#8b1634',
              cursor: (page===1 || filtered.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >Prev</button>
          <span style={{
            fontWeight: '700',
            color: '#8b1634',
            fontSize: '16px'
          }}>{page} / {totalPages}</span>
          <button 
            className="horizon-table-btn" 
            disabled={page===totalPages || filtered.length === 0} 
            onClick={()=>setPage(page+1)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              background: (page===totalPages || filtered.length === 0) ? '#f7fafc' : '#ffffff',
              color: (page===totalPages || filtered.length === 0) ? '#a0aec0' : '#8b1634',
              cursor: (page===totalPages || filtered.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >Next</button>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{maxWidth:'1200px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Add Work At Height</h5>
                <button type="button" className="btn-close" onClick={()=>setShowAddModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-2 col-md-6">
                  <label>Picos Name</label>
                  <input type="text" className="form-control" value={newRecord.picosName} onChange={e=>handleTextChange("picosName", e.target.value)} />
                </div>
                <div className="mb-2 col-md-6">
                  <label>Site</label>
                  <SearchableSiteSelect
                    value={newRecord.site}
                    options={sites.map((s) => ({ value: s.siteName, label: s.siteName }))}
                    onChange={(val) => handleTextChange("site", val)}
                    placeholder="Select site"
                  />
                </div>
                <div className="mb-2 col-md-6">
                  <label>Description</label>
                  <input type="text" className="form-control" value={newRecord.descriptionOfWah} onChange={e=>handleTextChange("descriptionOfWah", e.target.value)} />
                </div>
                <div className="mb-2 col-md-6">
                  <label>Date Issued</label>
                  <input type="date" className="form-control" value={convertDateForInput(newRecord.dateIssued)} onChange={e=>handleTextChange("dateIssued", convertDateForSave(e.target.value))} />
                </div>
                <div className="mb-2 col-md-6">
                  <label>Start Time</label>
                  <input type="time" className="form-control" value={newRecord.startTime} onChange={e=>handleTextChange("startTime", e.target.value)} />
                </div>
                <div className="mb-2 col-md-6">
                  <label>Finish Time</label>
                  <input type="time" className="form-control" value={newRecord.finishTime} onChange={e=>handleTextChange("finishTime", e.target.value)} />
                </div>
                <div className="mb-2 col-md-6">
                  <label>Checklist</label>
                  {newRecord.checklistSections.length > 0 ? (
                    newRecord.checklistSections.map((section, sidx) => (
                    <div key={sidx} style={{marginBottom:8}}>
                      <b>{section.header}</b>
                      {section.items.map((item, iidx) => (
                        <label key={iidx} style={{marginLeft:12, display: 'flex', alignItems: 'center', wordWrap: 'break-word'}}>
                          <input type="checkbox" checked={item.value} onChange={()=>handleChecklistChange(sidx, iidx)} style={{marginRight: '8px', flexShrink: 0}} /> <span style={{flex: 1}}>{item.title}</span>
                        </label>
                      ))}
                    </div>
                    ))
                  ) : (
                    <p style={{color: '#888', fontStyle: 'italic'}}>No checklist data available</p>
                  )}
                </div>
                <div className="mb-2 col-md-6">
                  <label className="form-label fw-semibold text-secondary">Signature</label>
                  <div style={{
                    width: '100%',
                    border: '2px solid #8b1634',
                    borderRadius: '18px',
                    background: '#e9cfd6',
                    padding: '0.5rem',
                    textAlign: 'left',
                    position: 'relative',
                    minHeight: '120px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      width: '100%',
                      height: '100px',
                      position: 'relative',
                      display: 'block'
                    }}>
                    <SignatureCanvas
                      ref={sigCanvasRef}
                      penColor="#8b1634"
                        backgroundColor="white"
                      clearOnResize={false}
                      velocityFilterWeight={0.7}
                      minWidth={1}
                      maxWidth={2.5}
                      dotSize={1.5}
                      throttle={16}
                        canvasProps={{
                          width: 500,
                          height: 80,
                          style: {
                            width: '100%',
                            height: '80px',
                            border: 'none',
                            borderRadius: '12px',
                            background: 'white',
                            display: 'block',
                            touchAction: 'none',
                            cursor: 'crosshair',
                            position: 'relative'
                          }
                        }}
                      onEnd={saveSignature}
                    />
                    </div>
                    <div style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: '8px'
                    }}>
                      <span style={{
                        fontSize: '12px',
                        color: '#8b1634',
                        fontStyle: 'italic'
                      }}>
                        Draw your signature above
                      </span>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm rounded-pill px-3"
                        style={{
                          background: 'transparent',
                          color: '#8b1634',
                          borderColor: '#8b1634'
                        }}
                        onClick={clearSignature}>
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowAddModal(false)}>Cancel</button>
                <button type="button" className="btn" style={{background:'#8b1634', color:'#fff', fontWeight:600}} onClick={handleAdd}>Submit</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editRecord && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.45)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ maxWidth: "1100px", margin: "0 auto", border: "none", borderRadius: 14, overflow: "hidden", boxShadow: "0 12px 40px rgba(0,0,0,0.18)" }}>
              <div className="modal-header" style={{ borderBottom: "none", padding: "16px 20px", background: `linear-gradient(135deg, ${WAH_THEME} 0%, #a01a3f 100%)` }}>
                <h5 className="modal-title" style={{ color: "#fff", fontWeight: 600, fontSize: 18, margin: 0 }}>Edit Work At Height</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)} />
              </div>
              <div className="modal-body" style={{ padding: "20px", background: "#fff", maxHeight: "75vh", overflowY: "auto" }}>
                <div className="row g-3 mb-1">
                  <div className="col-12 col-md-4">
                    <label style={wahEditLabelStyle}>Picos Name</label>
                    <input type="text" className="form-control" style={wahEditInputStyle} value={editRecord.picosName} onChange={(e) => handleTextChange("picosName", e.target.value, true)} />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={wahEditLabelStyle}>Site</label>
                    <input type="text" className="form-control" style={wahEditInputStyle} value={editRecord.site || ""} onChange={(e) => handleTextChange("site", e.target.value, true)} />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={wahEditLabelStyle}>Description</label>
                    <input type="text" className="form-control" style={wahEditInputStyle} value={editRecord.descriptionOfWah} onChange={(e) => handleTextChange("descriptionOfWah", e.target.value, true)} />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={wahEditLabelStyle}>Date Issued</label>
                    <div className="site-register-datepicker-wrap">
                      <DatePicker
                        {...siteRegisterDatePickerProps}
                        selected={parseSiteRegisterDate(editRecord.dateIssued)}
                        onChange={(d) => handleTextChange("dateIssued", formatSiteRegisterDate(d), true)}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <label style={wahEditLabelStyle}>Start Time</label>
                    <input type="time" className="form-control" style={wahEditInputStyle} value={editRecord.startTime} onChange={(e) => handleTextChange("startTime", e.target.value, true)} />
                  </div>
                  <div className="col-12 col-md-6">
                    <label style={wahEditLabelStyle}>Finish Time</label>
                    <input type="time" className="form-control" style={wahEditInputStyle} value={editRecord.finishTime} onChange={(e) => handleTextChange("finishTime", e.target.value, true)} />
                  </div>
                </div>
                <div className="mt-4">
                  {Array.isArray(editRecord.checklistSections) && editRecord.checklistSections.map((section, sectionIndex) => (
                    <div key={sectionIndex} className="mb-4" style={{ border: "1px solid #e8e8e8", borderRadius: 10, overflow: "hidden" }}>
                      <div style={{ padding: "10px 14px", background: "#f8f4f5", borderBottom: "1px solid #e8e8e8" }}>
                        <span style={{ fontWeight: 600, fontSize: 14, color: WAH_THEME }}>
                          {section.header || `Section ${sectionIndex + 1}`}
                        </span>
                      </div>
                      <div style={{ padding: "8px 14px 12px" }}>
                      {Array.isArray(section.items) && section.items.map((item, itemIndex) => (
                          <label
                            key={itemIndex}
                            className="d-flex align-items-start gap-3 mb-2"
                            style={{ cursor: "pointer", padding: "8px 10px", borderRadius: 8, transition: "background 0.15s" }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = "#faf8f9"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                          >
                            <input
                              type="checkbox"
                              className="form-check-input theme-checkbox mt-1 flex-shrink-0"
                              checked={Boolean(item.value)}
                              onChange={() => {
                                const updatedSections = [...editRecord.checklistSections];
                                updatedSections[sectionIndex].items[itemIndex].value = !item.value;
                                setEditRecord({ ...editRecord, checklistSections: updatedSections });
                              }}
                            />
                            <span style={{ flex: 1, fontSize: 14, lineHeight: 1.45, color: "#374151", wordBreak: "break-word" }}>
                              {item.title || `Item ${itemIndex + 1}`}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4">
                  <label style={wahEditLabelStyle}>Signature</label>
                  <div style={{
                    width: "100%",
                    border: `2px solid ${WAH_THEME}`,
                    borderRadius: 12,
                    background: "#f8f4f5",
                    padding: "12px",
                    textAlign: "center",
                    minHeight: 120,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    {editRecord.signature ? (
                      <img 
                        src={editRecord.signature.startsWith('data:image') 
                          ? editRecord.signature 
                          : `data:image/png;base64,${editRecord.signature}`}
                        alt="Signature" 
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100px',
                          objectFit: 'contain',
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          padding: '4px',
                          backgroundColor: 'white'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <span style={{ color: "#9ca3af", fontSize: 14 }}>No signature</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: "1px solid #eee", padding: "14px 20px", background: "#fafafa", gap: 10 }}>
                <button type="button" className="btn" style={{ background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 8, padding: "8px 20px", fontWeight: 600, minWidth: 96 }} onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="button" className="btn" style={{ background: WAH_THEME, color: "#fff", fontWeight: 600, borderRadius: 8, padding: "8px 24px", border: "none" }} onClick={handleEditSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SharePoint Upload Modal */}
      {showSharePointUploadModal && sharePointUploadRecord && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{maxWidth:'600px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Upload to SharePoint</h5>
                <button type="button" className="btn-close" onClick={()=>{
                  setShowSharePointUploadModal(false);
                  setSharePointUploadRecord(null);
                  setSharePointPhaseType('phase');
                  setSharePointPhaseNumber('15');
                  setSharePointNetwork('');
                }}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-3 col-12">
                    <label className="form-label fw-semibold">Type <span style={{color:'red'}}>*</span></label>
                    <select 
                      className="form-select" 
                      value={sharePointPhaseType}
                      onChange={(e) => {
                        setSharePointPhaseType(e.target.value);
                        if (e.target.value === 'non-phase') {
                          setSharePointNetwork('');
                        }
                      }}
                    >
                      <option value="phase">Phase</option>
                      <option value="non-phase">Non Phase</option>
                    </select>
                  </div>
                  
                  {sharePointPhaseType === 'phase' && (
                    <>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Phase Number <span style={{color:'red'}}>*</span></label>
                        <select 
                          className="form-select" 
                          value={sharePointPhaseNumber}
                          onChange={(e) => {
                            setSharePointPhaseNumber(e.target.value);
                          }}
                        >
                          <option value="15">Phase 15 (2025)</option>
                          <option value="16">Phase 16 (2026)</option>
                        </select>
                      </div>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Area <span style={{color:'red'}}>*</span></label>
                        <select 
                          className="form-select" 
                          value={sharePointNetwork}
                          onChange={(e) => {
                            setSharePointNetwork(e.target.value);
                          }}
                        >
                          <option value="">Select Area</option>
                          <option value="EPN">EPN</option>
                          <option value="LPN">LPN</option>
                          <option value="SPN">SPN</option>
                        </select>
                      </div>
                    </>
                  )}
                  
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={()=>{
                    setShowSharePointUploadModal(false);
                    setSharePointUploadRecord(null);
                    setSharePointPhaseType('phase');
                    setSharePointPhaseNumber('15');
                    setSharePointNetwork('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn" 
                  style={{background:'#8b1634', color:'#fff', fontWeight:600}}
                  onClick={handleSharePointUpload}
                >
                  Upload to SharePoint
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload Work At Height File"
        importPath="work-at-height/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setRecords((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{maxWidth:'1200px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Delete Work At Height</h5>
                <button type="button" className="btn-close" onClick={()=>setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this record?</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowDeleteModal(false)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default WorkAtHeightPage;
